import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FlaskConical, MicroscopeIcon, Users, FileSearch, CheckCircle, Droplets, Repeat, TestTube, Network, BrainCircuit, Microscope, UserCheck, BarChart4, Scale, ArrowRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export const ResearchPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 50
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  const researchProjects = [{
    id: 1,
    title: 'Probiotic Colonization Study',
    category: 'Mechanistic Research',
    description: 'Investigating the colonization potential of 15 commercial probiotic strains using advanced microbiome analysis.',
    image: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80'
  }, {
    id: 2,
    title: 'Microbiome Supplement Efficacy Trial',
    category: 'Clinical & Translational Studies',
    description: 'A 60-person study evaluating the effects of a prebiotic-probiotic combination on gut barrier function and inflammation markers.',
    image: 'https://images.unsplash.com/photo-1576086213369-97a306d36557?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80'
  }, {
    id: 3,
    title: 'Heavy Metal Analysis in Supplements',
    category: 'Product Validation Studies',
    description: 'Comprehensive testing of 50 popular supplements for heavy metal contamination and label accuracy.',
    image: 'https://images.unsplash.com/photo-1579154341098-e4e158cc7f55?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80'
  }];
  return <div className="bg-white w-full" data-id="element-1697" data-mp="3p9r67vx6nhkh">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-50" data-id="element-1698" data-mp="3kcsetl4wlopz">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1699" data-mp="3ka645cmput3j">
          <div className="text-center" data-id="element-1700" data-mp="2ufxssu9ecpu">
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl" data-id="element-1701" data-mp="2xlogcijdmno">
              Research at Ukweli Institute
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-600" data-id="element-1702" data-mp="36divicleiehs">
              Setting new standards in microbiome and therapeutic science with
              rigorous, independent research
            </p>
          </div>
        </div>
      </section>
      {/* Research Overview */}
      <section className="py-16 bg-white" data-id="element-1703" data-mp="3bbaz43verp1t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1704" data-mp="3ba9f39wezs7y">
          <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={sectionVariants} transition={{
          duration: 0.8
        }} className="mb-12" data-id="element-1705" data-mp="vjdg2vyh09il">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center" data-id="element-1706" data-mp="134gz37cssdwf">
              Our Research Approach
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto" data-id="element-1707" data-mp="p5himmzfv5bc">
              The Ukweli Institute conducts rigorous, independent research
              across multiple disciplines to provide objective evidence about
              supplements, probiotics, and other microbiome-targeted products.
              Under the leadership of{' '}
              <Link to="/profile" className="text-blue-600 hover:text-blue-800" data-id="element-1708" data-mp="1830lt9z7ttpu">
                Azn Alahah
              </Link>
              , our comprehensive approach builds a foundation of trust and
              scientific validity, unlike most supplement companies that do
              minimal research beyond marketing studies.
            </p>
          </motion.div>
          {/* Featured Research Projects */}
          <div className="mt-16 mb-16" data-id="element-1709" data-mp="gx4qnwldrjye">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center" data-id="element-1710" data-mp="gy6cmw3xpquw">
              Featured Research Projects
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1711" data-mp="6yfjaxr41hwe">
              {researchProjects.map((project, index) => <motion.div key={project.id} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-gray-50 rounded-lg overflow-hidden shadow-md" data-id="element-1712" data-mp="2xlve4do433i" data-mpi={index}>
                  <div className="h-48 overflow-hidden" data-id="element-1713" data-mp="1dmccl5pwvv2u">
                    <img src={project.image} alt={project.title} className="w-full h-full object-cover" data-id="element-1714" data-mp="1dkrkdmr2ko85" />
                  </div>
                  <div className="p-6" data-id="element-1715" data-mp="35aae9ab2dgex">
                    <div className="mb-2" data-id="element-1716" data-mp="35991n1eeglv3">
                      <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full uppercase font-semibold tracking-wide" data-id="element-1717" data-mp="35ceosb3gxdo6">
                        {project.category}
                      </span>
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1718" data-mp="3k6rn9uvs6iyb">
                      {project.title}
                    </h4>
                    <p className="text-gray-600 mb-4" data-id="element-1719" data-mp="3oz2z9w3spv78">{project.description}</p>
                    <Link to="#" className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium" data-id="element-1720" data-mp="3syl9nxxzqnvt">
                      Learn more <ArrowRightIcon size={16} className="ml-1" data-id="element-1721" data-mp="6tpws2qgg9r3" />
                    </Link>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Research Types */}
      <section className="py-16 bg-gray-50" data-id="element-1722" data-mp="1xuvocih1ab8x">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1723" data-mp="2cmnzei9sp34u">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-16" data-id="element-1724" data-mp="27v60s0v8ven1">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1725" data-mp="2vi4a8eky5mbg">
              Types of Research at Ukweli Institute
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-1726" data-mp="115o032p15o1b">
              Our multi-disciplinary approach ensures comprehensive evaluation
              of microbiome-targeted products
            </p>
          </motion.div>
          {/* Product Validation Studies */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="mb-20" data-id="element-1727" data-mp="3ctjebpb3agje">
            <div className="flex items-center mb-6" data-id="element-1728" data-mp="2p6b9f8hkp9hw">
              <div className="bg-blue-100 p-3 rounded-full mr-4" data-id="element-1729" data-mp="2u4cqjnvfuyqq">
                <FlaskConical size={28} className="text-blue-600" data-id="element-1730" data-mp="2f62b9p40tdzv" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900" data-id="element-1731" data-mp="2f8ezul4aamlw">
                1. Product Validation Studies
              </h3>
            </div>
            <p className="text-lg text-gray-700 mb-8 pl-16" data-id="element-1732" data-mp="dfqua5463n2y">
              We verify that supplements and therapeutic products are safe,
              effective, and truthfully labeled through rigorous analytical
              testing.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pl-16" data-id="element-1733" data-mp="32bm9rjfi8gt5">
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1734" data-mp="2xbi30l4ef0g4">
                <div className="flex items-center mb-4" data-id="element-1735" data-mp="2siye4s09giw0">
                  <CheckCircle size={20} className="text-blue-600 mr-2" data-id="element-1736" data-mp="2nkww8aekmpm4" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1737" data-mp="2nkn5ulpm6gfw">
                    Purity & Safety
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1738" data-mp="2ejpaert8rh8x">
                  Analytical chemistry to verify label claims (e.g., correct
                  dosage, no contaminants, heavy metals, or adulterants).
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1739" data-mp="24nlzawgew89u">
                <div className="flex items-center mb-4" data-id="element-1740" data-mp="7u0wtvcpt4w8">
                  <Droplets size={20} className="text-blue-600 mr-2" data-id="element-1741" data-mp="7rer819dwzgn" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1742" data-mp="2v7jq41cbfwl">
                    Bioavailability Testing
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1743" data-mp="fcs102gkzf18">
                  In vitro digestion and absorption models to test whether
                  compounds survive stomach acid and reach target tissues.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1744" data-mp="p1s683lof6nv">
                <div className="flex items-center mb-4" data-id="element-1745" data-mp="3nl8pjal97m5f">
                  <Repeat size={20} className="text-blue-600 mr-2" data-id="element-1746" data-mp="3nmtordiijt9m" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1747" data-mp="3skldxzrrx1a4">
                    Batch Consistency
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1748" data-mp="6cc112z1qq14">
                  Ongoing monitoring of supplement quality across different
                  manufacturing runs.
                </p>
              </div>
            </div>
          </motion.div>
          {/* Mechanistic Research */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="mb-20" data-id="element-1749" data-mp="1htv7r5bu5b4f">
            <div className="flex items-center mb-6" data-id="element-1750" data-mp="2lah47s6irtxd">
              <div className="bg-blue-100 p-3 rounded-full mr-4" data-id="element-1751" data-mp="1okzp6mlncvj0">
                <MicroscopeIcon size={28} className="text-blue-600" data-id="element-1752" data-mp="1omszcbvgxxvr" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900" data-id="element-1753" data-mp="1sml8apory61n">
                2. Mechanistic Research
              </h3>
            </div>
            <p className="text-lg text-gray-700 mb-8 pl-16" data-id="element-1754" data-mp="22k7tftwo2tu2">
              We investigate how products work at the cellular and molecular
              level, providing deeper understanding of their effects on the
              microbiome and human physiology.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pl-16" data-id="element-1755" data-mp="3e1hayaw8jbsq">
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1756" data-mp="12bj6b3542o5e">
                <div className="flex items-center mb-4" data-id="element-1757" data-mp="xcy8199f6ejl">
                  <TestTube size={20} className="text-blue-600 mr-2" data-id="element-1758" data-mp="xfkdn3cr2jz6" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1759" data-mp="skwtfgos4bqe">
                    In Vitro Models
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1760" data-mp="jmcjb3xabads">
                  Using petri dish assays, organoids, and 3D gut models to
                  examine whether supplements produce measurable biological
                  effects.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1761" data-mp="9pq16mwcexfg">
                <div className="flex items-center mb-4" data-id="element-1762" data-mp="4pu6je9jcick">
                  <Network size={20} className="text-blue-600 mr-2" data-id="element-1763" data-mp="241ej8xxvfg42" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1764" data-mp="23zk7a94kuyc4">
                    Pathway Testing
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1765" data-mp="3tiidydf3en3a">
                  Measuring molecular markers such as cytokine signaling, PKR
                  activity, mitochondrial stress responses, and barrier
                  integrity.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1766" data-mp="3ojp0um3pwy3n">
                <div className="flex items-center mb-4" data-id="element-1767" data-mp="3ok8n2f0ei8nh">
                  <BrainCircuit size={20} className="text-blue-600 mr-2" data-id="element-1768" data-mp="g3dtxr9eo2ml" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1769" data-mp="l2ypdrpd9suk">
                    Microbial Interaction Studies
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1770" data-mp="72o20yn9mwe5">
                  Assessing whether probiotics can colonize, disrupt biofilms,
                  or modulate harmful bacteria.
                </p>
              </div>
            </div>
          </motion.div>
          {/* Clinical & Translational Studies */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="mb-20" data-id="element-1771" data-mp="2vwcp76z3iap4">
            <div className="flex items-center mb-6" data-id="element-1772" data-mp="2tbn4hlwki1w4">
              <div className="bg-blue-100 p-3 rounded-full mr-4" data-id="element-1773" data-mp="25l0uvq6x09k0">
                <Users size={28} className="text-blue-600" data-id="element-1774" data-mp="25lj37l6uqe8a" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900" data-id="element-1775" data-mp="20n94j0lx4vhx">
                3. Clinical & Translational Studies
              </h3>
            </div>
            <p className="text-lg text-gray-700 mb-8 pl-16" data-id="element-1776" data-mp="2ak41hf2jmnu3">
              We conduct human trials to evaluate the real-world efficacy and
              safety of promising products in diverse populations.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pl-16" data-id="element-1777" data-mp="1hvyh4tfej0us">
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1778" data-mp="l41qq1h14tv2">
                <div className="flex items-center mb-4" data-id="element-1779" data-mp="l5wcfi04kq07">
                  <Microscope size={20} className="text-blue-600 mr-2" data-id="element-1780" data-mp="p551wao4u1py" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1781" data-mp="u4q4cv2igs7f">
                    Pilot Human Trials
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1782" data-mp="yw82zch2agp8">
                  Small-scale trials (n=30–60) to test tolerability and
                  preliminary efficacy of certified products.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1783" data-mp="18rt4l9i7xeyg">
                <div className="flex items-center mb-4" data-id="element-1784" data-mp="1cwal8zta7c50">
                  <Scale size={20} className="text-blue-600 mr-2" data-id="element-1785" data-mp="1huc0tari0tbq" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1786" data-mp="1htj30zaipe5q">
                    Comparative Studies
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1787" data-mp="3jkvvgovattkw">
                  Benchmarking multiple supplements in the same category to see
                  which ones deliver the strongest measurable effects.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1788" data-mp="379v7zkktphml">
                <div className="flex items-center mb-4" data-id="element-1789" data-mp="32blb95jga8yv">
                  <UserCheck size={20} className="text-blue-600 mr-2" data-id="element-1790" data-mp="32a0ct4u0m5vq" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1791" data-mp="2xeuvkjbk5fdz">
                    Subgroup Research
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1792" data-mp="3q1fldfcdxjff">
                  Exploring how supplements work in specific populations
                  (autism, IBS, long COVID, chronic fatigue).
                </p>
              </div>
            </div>
          </motion.div>
          {/* Meta-Research & Transparency */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-1793" data-mp="29o7b6ykggxaj">
            <div className="flex items-center mb-6" data-id="element-1794" data-mp="2c1ktoe2p0px0">
              <div className="bg-blue-100 p-3 rounded-full mr-4" data-id="element-1795" data-mp="1x5ldsh9cdlhb">
                <FileSearch size={28} className="text-blue-600" data-id="element-1796" data-mp="1x7fx5r4yt5ja" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900" data-id="element-1797" data-mp="2269drsfjtcno">
                4. Meta-Research & Transparency
              </h3>
            </div>
            <p className="text-lg text-gray-700 mb-8 pl-16" data-id="element-1798" data-mp="1t3qcvdvbpa42">
              We analyze existing research and conduct comparative studies to
              provide consumers and practitioners with clear, unbiased
              information.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pl-16" data-id="element-1799" data-mp="1jbko0d0db8t3">
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1800" data-mp="15efkajo4mm10">
                <div className="flex items-center mb-4" data-id="element-1801" data-mp="19g0vm0l0is93">
                  <FileSearch size={20} className="text-blue-600 mr-2" data-id="element-1802" data-mp="19hlkpaok5izk" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1803" data-mp="qrqmcm9iwgwq">
                    Evidence Reviews
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1804" data-mp="zrn6cakywrlg">
                  Analyzing and publishing transparent reports on existing
                  literature for widely used supplements.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1805" data-mp="19p9nl3pqov8l">
                <div className="flex items-center mb-4" data-id="element-1806" data-mp="14qzquood9mkv">
                  <BarChart4 size={20} className="text-blue-600 mr-2" data-id="element-1807" data-mp="14qgju3098ilm" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1808" data-mp="1iimn4oib6v3n">
                    Consumer Reports-Style Studies
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1809" data-mp="1sgkal9scgnv7">
                  Head-to-head product comparisons with open publication of
                  data.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1810" data-mp="1nfd4rg9rhyr6">
                <div className="flex items-center mb-4" data-id="element-1811" data-mp="2ut58xmaj4hyt">
                  <CheckCircle size={20} className="text-blue-600 mr-2" data-id="element-1812" data-mp="2yv8keax77mk3" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1813" data-mp="2yxl9d81dix6n">
                    Certification Integration
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1814" data-mp="38sd9ojmcby54">
                  Feeding results back into the Ukweli Certified™ three-tier
                  system (Evidence Screen → Mechanistic Validation → Clinical
                  Validation).
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Why This Matters */}
      <section className="py-16 bg-white" data-id="element-1815" data-mp="2pvc2qt987nyj">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1816" data-mp="2pweqisb95aa8">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-blue-50 rounded-xl p-8 md:p-12" data-id="element-1817" data-mp="1sg7zr1dmiqst">
            <div className="flex items-center mb-6" data-id="element-1818" data-mp="1fw170s8dfs0o">
              <div className="bg-blue-600 p-2 rounded-full mr-4 text-white" data-id="element-1819" data-mp="1fyx3ec4km8n0">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-1820" data-mp="1ax7wn8odsh4q">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" data-id="element-1821" data-mp="x6cjvlarhfvv" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900" data-id="element-1822" data-mp="n7nakgwxl3r8">
                Why This Matters
              </h3>
            </div>
            <p className="text-lg text-gray-700 mb-6" data-id="element-1823" data-mp="j8xps39kcjxk">
              Most supplement companies do minimal or no research beyond
              marketing studies. By carrying out its own in-house validation and
              independent trials, the Ukweli Institute:
            </p>
            <ul className="space-y-4 text-gray-700" data-id="element-1824" data-mp="2v9xli5oo5cdw">
              <li className="flex items-start" data-id="element-1825" data-mp="2q9jb4gzlhqcp">
                <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 mr-3" data-id="element-1826" data-mp="2qaueifp4ow3a">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" data-id="element-1827" data-mp="2giexplpkidjz">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" data-id="element-1828" data-mp="2bhralb4gmgv1" />
                  </svg>
                </div>
                <p className="text-lg" data-id="element-1829" data-mp="d20j21h3ymyy">
                  <strong data-id="element-1830" data-mp="d0yvixiwo80c">Provides objective evidence</strong> that products are
                  safe and effective.
                </p>
              </li>
              <li className="flex items-start" data-id="element-1831" data-mp="n07wwbg3oowq">
                <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 mr-3" data-id="element-1832" data-mp="n1j0aa5mvunb">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" data-id="element-1833" data-mp="qzp1l2npdi0u">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" data-id="element-1834" data-mp="3vgsy362hqdpa" />
                  </svg>
                </div>
                <p className="text-lg" data-id="element-1835" data-mp="37px88ehlhsko">
                  <strong data-id="element-1836" data-mp="3bs0kv6g0337j">Creates a trustworthy benchmark</strong> for
                  consumers, clinicians, and regulators.
                </p>
              </li>
              <li className="flex items-start" data-id="element-1837" data-mp="ivpe307j5gc1">
                <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 mr-3" data-id="element-1838" data-mp="dyyxmbrtciq3">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" data-id="element-1839" data-mp="sm1xuq7it5h4">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" data-id="element-1840" data-mp="3w3k0mj8frmao" />
                  </svg>
                </div>
                <p className="text-lg" data-id="element-1841" data-mp="4xiverb5jmwy">
                  <strong data-id="element-1842" data-mp="4zvej7snc3bl">Builds a feedback loop</strong> where research →
                  certification → consumer guidance → higher industry standards.
                </p>
              </li>
            </ul>
          </motion.div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-gray-50" data-id="element-1843" data-mp="1nuvqz4k085gi">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1844" data-mp="1sv9zsotfjjfl">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-1845" data-mp="1st6z9auni7wm">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1846" data-mp="2bmaw153610vg">
              Partner with Us on Research
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-8" data-id="element-1847" data-mp="1wwlrczvv6eqf">
              Interested in collaborating on research or having your products
              evaluated by our independent team? Contact us to learn more about
              our research partnerships and certification process.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1848" data-mp="2udu07f8xekz2">
              <Link to="/contact" className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow-md hover:bg-blue-700 transition-colors duration-300" data-id="element-1849" data-mp="16oz2tjmxxdzt">
                Contact Our Research Team
              </Link>
              <Link to="/certification" className="px-6 py-3 bg-white text-blue-600 font-medium rounded-md shadow-md hover:bg-gray-100 transition-colors duration-300" data-id="element-1850" data-mp="rrq8ch2cbu3v">
                Learn About Certification
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
};